import { DashboardState, DesignerMessageType, ESdChangeReason, type IRenderFrameMessage } from "@kuika/kuika-cl-model";
import { PatchOp, StagedEntity } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import { EJsonEntityType, ENTITY_TYPE_DISPLAY_NAMES } from "@kuika/kuika-cl-model/src/enums/entity-type";
import React, { useCallback, useEffect, useRef, useState } from "react";
import BranchingService from "../../../services/branching-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { SaveStatus } from "../../../shared/utility/save-status";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaLoader from "../../ui-kit/kuika-loader/kuika-loader";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { BranchChangeCaption, DiffPatchOp, generateScreenDesignCaptions } from "./branch-change-captions";
import { generateWorkflowCaptions, workflowChangedNodeIds } from "./branch-workflow-captions";
import CommitToBranchModal, { CommitModalPayload } from "./commit-to-branch-modal";
import DiscardChangesModal from "./discard-changes-modal";
import "./branching-staged-changes.scss";
import { EntityDiffCard } from "./entity-diff-card";
import { parseSnapshot } from "./entity-field-extractors";
import { WorkflowBpmnDiff } from "./workflow-bpmn-diff";

export interface IBranchingStagedChangesProps {
  branchId: string;
  onAfterDiscard?: () => void;
  /** When provided, the viewer renders these entities instead of fetching the branch's staged
   *  changes. Used by the read-only merge-request review to reuse this exact viewer. */
  itemsOverride?: StagedEntity[];
  /** Read-only mode: hides every write control (Commit All / Revert). Otherwise identical. */
  readOnly?: boolean;
}

export interface IBranchingStagedChangesState {
  loading: boolean;
  items: StagedEntity[];
  selected: StagedEntity | null;
  previewSpec: { section: number | "consolidated"; mode: "before" | "after" } | null;
  iframeEntityKey: number;
  previewNonce: number;
  previewMode: "before" | "after";
  previewLoading: boolean;
  expandedChangelogIdx: number | null;
  actionLoading: string | null;
  showCommitModal: boolean;
  pendingCommitOpIds: string[];
  activeDiffTab: "consolidated" | "changelogs";
  inspectorOpen: boolean;
  inspectorWidth: number;
  inspectorResizing: boolean;
  pendingDiscard: PendingDiscard | null;
  /**
   * Where the open dialog's commit stands — the commit or the discard confirmation; only one is
   * ever open. Owned by `SaveStatus`, never set directly.
   */
  dialogStatus: KuikaButtonStatus;
}

/** How a discard ended: it ran and landed, it had nothing to run on, or the service refused it. */
type DiscardOutcome = "discarded" | "skipped" | "failed";

// A destructive discard-of-staged-changes awaiting user confirmation.
type PendingDiscard = { kind: "all"; count: number } | { kind: "changelog"; idx: number; count: number };

const INSPECTOR_MIN_W = 320;
const INSPECTOR_MAX_W = 760;
const INSPECTOR_DEFAULT_W = 400;

/** Entity types where Xml is a redundant derivative of Json and should be hidden from diffs. */
const ENTITY_TYPES_WITH_REDUNDANT_XML = new Set([
  EJsonEntityType.KWorkFlow,
  EJsonEntityType.KApiManagement,
  EJsonEntityType.KScheduledJobs
]);

function filterRedundantXmlOps(ops: PatchOp[], entityType?: string | number): PatchOp[] {
  if (entityType == null || !ENTITY_TYPES_WITH_REDUNDANT_XML.has(Number(entityType))) return ops;
  return ops.filter((op) => {
    const lower = op.path.toLowerCase();
    return lower !== "/xml" && !lower.startsWith("/xml/");
  });
}

function buildConsolidated(ops: PatchOp[]) {
  const order: string[] = [];
  const map = new Map<string, { op: string; path: string; previous: any; value: any }>();
  for (const p of ops) {
    if (!map.has(p.path)) {
      order.push(p.path);
      map.set(p.path, { op: p.op, path: p.path, previous: p.previous, value: p.value });
    } else {
      const row = map.get(p.path)!;
      row.value = p.value;
      if (row.op === "add" && p.op === "remove") row.op = "remove";
      else if (p.op !== "add") row.op = p.op;
    }
  }
  return order.map((k) => map.get(k)!);
}

function groupByChangelog(ops: PatchOp[]) {
  const order: string[] = [];
  const map = new Map<string, { changeId: string; author?: string; timestampUtc?: string; ops: PatchOp[] }>();
  for (const op of ops) {
    const id = op.changeId ?? "__unknown__";
    if (!map.has(id)) {
      order.push(id);
      map.set(id, { changeId: id, author: op.changeAuthor, timestampUtc: op.changeTimestampUtc, ops: [] });
    }
    map.get(id)!.ops.push(op);
  }
  return order.map((id) => map.get(id)!);
}

function extractChangedKuikaIds(ops: PatchOp[], snapshot: any): ChangedId[] {
  if (!snapshot || !ops?.length) return [];
  const design = normalizeDesign(snapshot);
  if (!design || !Array.isArray(design.displayItems)) return [];

  const kinds = new Map<string, ChangeKind>();
  const addKind = (id: string, kind: ChangeKind) => {
    const prev = kinds.get(id);
    if (prev === undefined) {
      kinds.set(id, kind);
      return;
    }
    // Structural changes (added/removed) take precedence over plain modifications.
    if (prev === "modified" && kind !== "modified") kinds.set(id, kind);
  };
  for (const op of ops) {
    const normalized = (op.path ?? "").replace(/^\/JSON\//, "").replace(/^\//, "");

    // Try to resolve the node at the exact path, then climb ancestors until
    // we find an object with a kuikaId or id. This handles nested child edits.
    try {
      let node = getValueAtPath(design, normalized);
      let cursorPath = normalized;
      while ((node == null || typeof node !== "object" || (node.kuikaId == null && node.id == null)) && cursorPath) {
        const segs = cursorPath.split("/").filter(Boolean);
        if (!segs.length) break;
        segs.pop();
        cursorPath = segs.join("/");
        node = cursorPath ? getValueAtPath(design, cursorPath) : undefined;
      }
      if (
        node &&
        (node.kuikaId != null || node.id != null || node.displayItemId != null || node.displayItemID != null)
      ) {
        // Prefer an integer display item id when available (agent expects
        // `#kuika_<displayItem_id>` where displayItem_id is an integer).
        const cand = node.displayItemId ?? node.displayItemID ?? node.id ?? node.kuikaId;
        let chosen: string;
        if (cand != null && (typeof cand === "number" || /^\d+$/.test(String(cand)))) {
          chosen = String(parseInt(String(cand), 10));
        } else {
          // If the candidate is non-numeric (e.g. a GUID), try to locate the
          // enclosing top-level displayItem and use its numeric id (or index)
          // which the preview agent expects.
          let fallback: string | null = null;
          try {
            const found = findNearestDisplayItemWithNumericId(design, node);
            if (found) {
              fallback = found.numericId;
            }
          } catch {}
          chosen = fallback ?? String(cand);
        }
        addKind(chosen, opToChangeKind(op));
        continue;
      }
    } catch {}

    // Fallback: if we can detect the top-level displayItems index, use that
    const m = normalized.match(/displayItems\/(\d+)/);
    if (m) {
      const idx = parseInt(m[1], 10);
      const di = design.displayItems[idx];
      if (di != null) {
        const cand = di.displayItemId ?? di.displayItemID ?? di.id ?? di.kuikaId;
        if (cand != null) {
          const chosen =
            typeof cand === "number" || /^\d+$/.test(String(cand)) ? String(parseInt(String(cand), 10)) : String(cand);
          addKind(chosen, opToChangeKind(op));
        }
      }
    }
  }

  return Array.from(kinds, ([id, kind]) => ({ id, kind }));
}

// Resolve one path segment against cursor.
// Handles id-keyed selectors ([id=VALUE]), numeric indices, and property names.
function resolveSegment(cursor: any, seg: string): any {
  if (cursor == null) return undefined;
  const idMatch = seg.match(/^\[id=(.+)\]$/);
  if (idMatch) {
    const idVal = idMatch[1];
    if (!Array.isArray(cursor)) return undefined;
    return cursor.find(
      (el: any) =>
        el != null &&
        (String(el.id) === idVal ||
          String(el.kuikaId) === idVal ||
          String(el.displayItemId) === idVal ||
          String(el.displayItemID) === idVal)
    );
  }
  if (Array.isArray(cursor) && /^\d+$/.test(seg)) return cursor[parseInt(seg, 10)];
  return cursor[seg];
}

// Returns the array index matching a segment, or -1.
function resolveArrayIndex(arr: any[], seg: string): number {
  const idMatch = seg.match(/^\[id=(.+)\]$/);
  if (idMatch) {
    const idVal = idMatch[1];
    return arr.findIndex(
      (el: any) =>
        el != null &&
        (String(el.id) === idVal ||
          String(el.kuikaId) === idVal ||
          String(el.displayItemId) === idVal ||
          String(el.displayItemID) === idVal)
    );
  }
  if (/^\d+$/.test(seg)) return parseInt(seg, 10);
  return -1;
}

function applyOpsToDesign(design: any, ops: PatchOp[]): any {
  if (!design) return design;
  const copy = JSON.parse(JSON.stringify(design));
  for (const op of ops) {
    const rawPath = (op.path ?? "").replace(/^\/JSON\//, "").replace(/^\//, "");
    const segments = rawPath.split("/").filter(Boolean);
    if (!segments.length) continue;
    try {
      let cursor: any = copy;
      for (let i = 0; i < segments.length - 1; i++) {
        cursor = resolveSegment(cursor, segments[i]);
        if (cursor == null) break;
      }
      if (cursor == null) continue;
      const last = segments[segments.length - 1];
      const isIdSeg = /^\[id=/.test(last);
      if (isIdSeg && Array.isArray(cursor)) {
        const idx = resolveArrayIndex(cursor, last);
        if (op.op === "remove") {
          if (idx >= 0) cursor.splice(idx, 1);
        } else if (op.op === "add") {
          cursor.push(op.value); // new id-keyed element → append
        } else {
          if (idx >= 0) cursor[idx] = op.value;
        }
      } else {
        const isNumIdx = /^\d+$/.test(last) && Array.isArray(cursor);
        const idx = isNumIdx ? parseInt(last, 10) : -1;
        if (op.op === "remove") {
          isNumIdx ? cursor.splice(idx, 1) : delete cursor[last];
        } else if (op.op === "add") {
          isNumIdx ? cursor.splice(idx, 0, op.value) : (cursor[last] = op.value);
        } else {
          isNumIdx ? (cursor[idx] = op.value) : (cursor[last] = op.value);
        }
      }
    } catch {}
  }
  return copy;
}

function getValueAtPath(design: any, path: string): any {
  if (!design) return undefined;
  const rawPath = (path ?? "").replace(/^\/JSON\//, "").replace(/^\//, "");
  const segments = rawPath.split("/").filter(Boolean);
  if (!segments.length) return undefined;
  try {
    let cursor: any = design;
    for (const s of segments) {
      cursor = resolveSegment(cursor, s);
      if (cursor == null) return undefined;
    }
    return cursor;
  } catch {
    return undefined;
  }
}

// Normalize a snapshot-like value into a design object that contains
// `displayItems` (array) when possible. This handles cases where the
// snapshot may be: the design object, a wrapper with JSON/Json/json string,
// a wrapper with JSON object, or fields where `displayItems` is itself
// stringified. Returns the parsed design or null when normalization fails.
function normalizeDesign(snap: any): any | null {
  if (!snap) return null;

  const tryParse = (v: any): any | null => {
    if (!v) return null;
    if (Array.isArray(v.displayItems)) return v;
    if (typeof v === "string") {
      try {
        const parsed = JSON.parse(v);
        return tryParse(parsed) ?? parsed;
      } catch {
        return null;
      }
    }
    if (typeof v === "object") {
      // If displayItems is a stringified JSON, attempt to parse it.
      try {
        if (typeof v.displayItems === "string") {
          try {
            const parsedDI = JSON.parse(v.displayItems);
            if (Array.isArray(parsedDI)) v = { ...v, displayItems: parsedDI };
            else if (Array.isArray(parsedDI?.displayItems)) v = { ...v, displayItems: parsedDI.displayItems };
          } catch {}
        }
      } catch {}
      if (Array.isArray(v.displayItems)) return v;
    }
    return null;
  };

  // 1) If the snap is already the design
  const direct = tryParse(snap);
  if (direct) return direct;

  // 2) If snap contains JSON/Json/json wrappers
  const raw = snap?.JSON ?? snap?.Json ?? snap?.json;
  if (raw != null) {
    const fromRaw =
      tryParse(raw) ??
      (typeof raw === "string"
        ? (() => {
            try {
              return JSON.parse(raw);
            } catch {
              return null;
            }
          })()
        : raw);
    if (fromRaw) return tryParse(fromRaw) ?? fromRaw;
  }

  // 3) As a last resort, try parsing the whole snap if it's a string
  if (typeof snap === "string") {
    try {
      const parsed = JSON.parse(snap);
      return tryParse(parsed) ?? parsed;
    } catch {}
  }

  // 4) Deep-search for an inner object that looks like a design (first found)
  try {
    const queue: any[] = [snap];
    const visited = new Set<any>();
    while (queue.length) {
      const cur = queue.shift();
      if (!cur || typeof cur !== "object" || visited.has(cur)) continue;
      visited.add(cur);
      const p = tryParse(cur);
      if (p) return p;
      for (const k of Object.keys(cur)) {
        const v = cur[k];
        if (v && typeof v === "object") queue.push(v);
        if (typeof v === "string") {
          try {
            queue.push(JSON.parse(v));
          } catch {}
        }
      }
    }
  } catch {}

  return null;
}

function findNearestDisplayItemWithNumericId(design: any, target: any): { numericId: string; topIndex: number } | null {
  if (!design || !Array.isArray(design.displayItems)) return null;

  const tryGetNumericId = (node: any): number | null => {
    if (!node || typeof node !== "object") return null;
    const cand = node.displayItemId ?? node.displayItemID ?? node.id ?? node.kuikaId;
    if (cand == null) return null;
    if (typeof cand === "number") return cand;
    if (/^\d+$/.test(String(cand))) return parseInt(String(cand), 10);
    return null;
  };

  // DFS search that returns the ancestor stack when target is found.
  const findStack = (root: any): any[] | null => {
    const visited = new Set<any>();
    const stack: any[] = [];
    const rec = (node: any): boolean => {
      if (node == null || typeof node !== "object") return false;
      if (visited.has(node)) return false;
      visited.add(node);
      stack.push(node);
      if (node === target) return true;
      if (Array.isArray(node)) {
        for (const el of node) {
          if (rec(el)) return true;
        }
      } else {
        for (const k of Object.keys(node)) {
          try {
            if (rec(node[k])) return true;
          } catch {}
        }
      }
      stack.pop();
      return false;
    };
    return rec(root) ? [...stack] : null;
  };

  for (let i = 0; i < design.displayItems.length; i++) {
    const di = design.displayItems[i];
    const stack = findStack(di);
    if (!stack) continue;
    // Walk stack from nearest ancestor (end) to root (start) and find the
    // closest node that has a numeric displayItem/id.
    for (let j = stack.length - 1; j >= 0; j--) {
      const n = stack[j];
      const nid = tryGetNumericId(n);
      if (nid != null) return { numericId: String(nid), topIndex: i };
    }
    // If none found in ancestors, try the top-level display item itself
    const topId = tryGetNumericId(di);
    if (topId != null) return { numericId: String(topId), topIndex: i };
    // else fallback to top index (as last resort)
    return { numericId: String(i), topIndex: i };
  }
  return null;
}

function formatPath(path: string): string {
  return (path ?? "/")
    .replace(/^\/JSON\//, "")
    .replace(/^\//, "")
    .replace(/\/(\d+)(\/|$)/g, "[$1]$2")
    .replace(/\//g, " › ");
}

function renderValue(val: any): React.ReactNode {
  if (val === undefined || val === null) return <span className="branching-staged__preview-dash">—</span>;
  if (typeof val === "object")
    return <pre className="branching-staged__preview-pre">{JSON.stringify(val, null, 2)}</pre>;
  return String(val);
}

/**
 * Resolve a JSON-patch path to a more human-friendly breadcrumb using snapshot
 * data where available. Falls back to `formatPath` when resolution isn't possible.
 */
function resolvePath(path: string, snapshotAfter?: any, snapshotBefore?: any): string {
  if (!path) return "/";
  if (!snapshotAfter && !snapshotBefore) return formatPath(path);
  const segments = (path ?? "/")
    .replace(/^\/JSON\//, "")
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean);
  if (!segments.length) return "/";

  // Build a cursor from a snapshot, handling all wrapper formats.
  const buildCursor = (snap: any): any => {
    if (!snap) return null;
    const norm = normalizeDesign(snap);
    if (norm) return norm;
    const raw = snap?.JSON ?? snap?.Json ?? snap?.json;
    try {
      return raw && typeof raw === "string" ? JSON.parse(raw) : (raw ?? snap);
    } catch {
      return null;
    }
  };

  let cursorAfter: any = buildCursor(snapshotAfter);
  let cursorBefore: any = buildCursor(snapshotBefore);
  const isSelectorSegment = (seg?: string) => !!seg && (/^\d+$/.test(seg) || /^\[id=.+\]$/.test(seg));
  const isStructuralContainer = (seg?: string) => {
    if (!seg) return false;
    // Hide noisy structural collection keys, but keep semantic array properties
    // (e.g. typoIds, values, options) visible in the breadcrumb.
    return ["displayItems", "children", "items"].includes(seg);
  };

  // Try to parse a value that may be a JSON-serialised array/object
  // (IDisplayItem stores children as embedded JSON strings in some server responses).
  const tryUnwrap = (v: any): any => {
    if (typeof v !== "string") return v;
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  };

  // Resolve an object to its best human-readable label.
  // Priority: direct name fields → IDisplayItemComponent.name via .item → numeric id.
  const getFriendly = (it: any): string | null => {
    if (!it || typeof it !== "object") return null;
    const nameFields = ["friendlyName", "entityLabel", "name", "title", "label", "description"];
    for (const c of nameFields) {
      if (typeof it[c] === "string" && it[c].trim()) return it[c];
    }
    // IDisplayItem wraps its component in `.item` (IDisplayItemComponent has name?: string)
    const inner = it.item && typeof it.item === "object" && !Array.isArray(it.item) ? it.item : null;
    if (inner) {
      for (const c of nameFields) {
        if (typeof inner[c] === "string" && inner[c].trim()) return inner[c];
      }
    }
    // Last resort: numeric id gives at least "#42" instead of "[3]"
    if (typeof it.id === "number") return `#${it.id}`;
    return null;
  };

  const resolved: string[] = [];
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];

    // Ensure cursors are parsed objects/arrays, not stale JSON strings
    if (typeof cursorAfter === "string") cursorAfter = tryUnwrap(cursorAfter);
    if (typeof cursorBefore === "string") cursorBefore = tryUnwrap(cursorBefore);

    if (isSelectorSegment(seg)) {
      // Supports both numeric array indices and id selectors like [id=526263].
      const itemAfter = Array.isArray(cursorAfter) ? resolveSegment(cursorAfter, seg) : undefined;
      const itemBefore = Array.isArray(cursorBefore) ? resolveSegment(cursorBefore, seg) : undefined;
      const item = itemAfter ?? itemBefore;
      const friendly = getFriendly(item);
      resolved.push(friendly ?? seg);
      cursorAfter = itemAfter;
      cursorBefore = itemBefore;
      continue;
    }

    // Non-numeric segment: skip if it is a container for the next indexed segment
    const nextSeg = segments[i + 1];
    let nextAfter = cursorAfter?.[seg];
    let nextBefore = cursorBefore?.[seg];
    // Unwrap JSON strings (e.g. serialised children arrays)
    if (typeof nextAfter === "string" && isSelectorSegment(nextSeg)) nextAfter = tryUnwrap(nextAfter);
    if (typeof nextBefore === "string" && isSelectorSegment(nextSeg)) nextBefore = tryUnwrap(nextBefore);

    if (
      isStructuralContainer(seg) &&
      isSelectorSegment(nextSeg) &&
      (Array.isArray(nextAfter) || Array.isArray(nextBefore))
    ) {
      // Skip structural container labels (displayItems/children/items),
      // but keep semantic property names like typoIds in the breadcrumb.
      cursorAfter = Array.isArray(nextAfter) ? nextAfter : undefined;
      cursorBefore = Array.isArray(nextBefore) ? nextBefore : undefined;
      continue;
    }

    resolved.push(seg);
    cursorAfter = nextAfter !== undefined ? nextAfter : cursorAfter?.[seg];
    cursorBefore = nextBefore !== undefined ? nextBefore : cursorBefore?.[seg];
  }
  return resolved.join(" › ");
}

type WorkflowDiffView = "summary" | "visual" | "details";

// Reads the BPMN diagram XML from a workflow snapshot (top-level `xml`/`Xml`).
function workflowXml(data: Record<string, unknown> | null): string | undefined {
  if (!data) return undefined;
  const value: unknown = data.xml ?? data.Xml;
  return typeof value === "string" && value.trim() ? value : undefined;
}

// Workflow changes shown three ways: a plain-language Summary ("Step 'Approve' renamed to 'Review'"),
// a Visual side-by-side BPMN diff (for large restructures), and the raw field-card Details — instead
// of a raw node-tree JSON blob.
function WorkflowChangeCaptions({
  selected,
  items
}: {
  selected: StagedEntity;
  items: StagedEntity[];
}): React.ReactElement {
  const [view, setView] = useState<WorkflowDiffView>("summary");
  const beforeData: Record<string, unknown> | null = parseSnapshot(selected.beforeSnapshot);
  const afterData: Record<string, unknown> | null = parseSnapshot(selected.afterSnapshot);
  const captions: BranchChangeCaption[] = generateWorkflowCaptions(beforeData, afterData);

  const card: React.ReactElement = (
    <EntityDiffCard
      entityType={Number(selected.entityType)}
      beforeSnapshot={selected.beforeSnapshot}
      afterSnapshot={selected.afterSnapshot}
      mode="staged"
      allStagedEntities={items}
    />
  );

  // No recognized structural change — fall back to the field card.
  if (captions.length === 0) return <div>{card}</div>;

  const beforeXml: string | undefined = workflowXml(beforeData);
  const afterXml: string | undefined = workflowXml(afterData);
  const canVisualize: boolean = !!beforeXml || !!afterXml;

  return (
    <div className="branching-staged__wf">
      <KuikaTabs
        size="sm"
        value={view}
        onChange={(v) => setView(v as WorkflowDiffView)}
        options={[
          { value: "summary", label: "Summary" },
          ...(canVisualize ? [{ value: "visual", label: "Visual diff" }] : []),
          { value: "details", label: "Details" }
        ]}
      />
      {view === "summary" && (
        <ul className="branching-staged__caption-list">
          {captions.map((c) => (
            <li key={c.id} className="branching-staged__caption-item">
              <span className="branching-staged__caption-dot" data-kind={c.kind} aria-hidden="true" />
              <span className="branching-staged__caption-text">{c.caption}</span>
            </li>
          ))}
        </ul>
      )}
      {view === "visual" && canVisualize && (
        <WorkflowBpmnDiff
          beforeXml={beforeXml}
          afterXml={afterXml}
          changedNodeIds={workflowChangedNodeIds(beforeData, afterData)}
        />
      )}
      {view === "details" && card}
    </div>
  );
}

function DiffTable({
  ops,
  snapshotAfter,
  snapshotBefore,
  entityType
}: {
  ops: PatchOp[];
  snapshotAfter?: any;
  snapshotBefore?: any;
  entityType?: string | number;
}) {
  const [showRaw, setShowRaw] = useState(false);
  if (!ops.length) return <div className="branching-staged__preview-empty">No property changes</div>;

  // Screen designs get plain-language captions ("Button \"SaveButton\" text changed from …") instead
  // of a JSON-pointer property table; the raw table stays available behind a technical-details toggle.
  const isScreenDesign: boolean = entityType != null && Number(entityType) === EJsonEntityType.KScreenDesign;
  if (isScreenDesign && !showRaw) {
    const design = normalizeDesign(snapshotAfter) ?? normalizeDesign(snapshotBefore);
    const captionOps: DiffPatchOp[] = ops.map((o) => ({
      op: o.op,
      path: o.path,
      value: o.value,
      previous: o.previous
    }));
    const captions: BranchChangeCaption[] = design ? generateScreenDesignCaptions(design, captionOps) : [];
    if (captions.length > 0) {
      return (
        <div className="branching-staged__captions">
          <ul className="branching-staged__caption-list">
            {captions.map((c) => (
              <li key={c.id} className="branching-staged__caption-item">
                <span className="branching-staged__caption-dot" data-kind={c.kind} aria-hidden="true" />
                <span className="branching-staged__caption-text">{c.caption}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="branching-staged__caption-rawtoggle" onClick={() => setShowRaw(true)}>
            Show technical details
          </button>
        </div>
      );
    }
  }

  return (
    <div>
      <div className="branching-staged__preview-wrap">
        <table className="branching-staged__preview-table">
          <thead>
            <tr>
              <th className="branching-staged__preview-th">Property</th>
              <th className="branching-staged__preview-th branching-staged__preview-th--before">Before</th>
              <th className="branching-staged__preview-th branching-staged__preview-th--after">After</th>
            </tr>
          </thead>
          <tbody>
            {ops.map((p, i) => {
              const friendlyLabel = resolvePath(p.path, snapshotAfter, snapshotBefore);
              const rawPath = formatPath(p.path);
              const showTooltip = friendlyLabel !== rawPath;
              return (
                <tr key={p.opId ?? p.path + i} className="branching-staged__preview-tr">
                  <td className="branching-staged__preview-td branching-staged__preview-td--path">
                    <KuikaTooltip content={showTooltip ? rawPath : null} position="top" align="start">
                      <span>{friendlyLabel}</span>
                    </KuikaTooltip>
                  </td>
                  <td
                    className="branching-staged__preview-td branching-staged__preview-td--before"
                    data-filled={p.previous != null || undefined}
                  >
                    {renderValue(p.previous)}
                  </td>
                  <td
                    className="branching-staged__preview-td branching-staged__preview-td--after"
                    data-filled={p.value != null || undefined}
                  >
                    {renderValue(p.value)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isScreenDesign && (
        <button type="button" className="branching-staged__caption-rawtoggle" onClick={() => setShowRaw(false)}>
          Hide technical details
        </button>
      )}
    </div>
  );
}

function SectionPreview({
  iframeKey,
  spec,
  getSnapshot,
  onLoaded,
  kuikaIds,
  changeKinds,
  nonce,
  allOps
}: {
  iframeKey: number;
  spec: { section: number | "consolidated"; mode: "before" | "after" } | null;
  getSnapshot: (s: number | "consolidated", m: "before" | "after") => any;
  onLoaded?: () => void;
  kuikaIds?: string[];
  changeKinds?: Record<string, ChangeKind>;
  nonce?: number;
  allOps?: PatchOp[];
}) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const highlightTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);
  const [highlightBox, setHighlightBox] = useState<null | {
    left: number;
    top: number;
    width: number;
    height: number;
    ts: number;
  }>(null);
  const [highlightTargetId, setHighlightTargetId] = useState<string | null>(null);
  const [hoverLabel, setHoverLabel] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const highlightUpdaterRef = useRef<number | null>(null);
  const highlightClearTimeoutRef = useRef<number | null>(null);
  const [pinned, setPinned] = useState<boolean>(true);
  const [visible, setVisible] = useState<boolean>(true);
  const [zoom, setZoom] = useState<number>(1);
  const [hunkIndex, setHunkIndex] = useState<number>(0);

  const ZOOM_MIN = 0.25;
  const ZOOM_MAX = 2;
  const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 20) / 20));
  const adjustZoom = (delta: number) => setZoom((z) => clampZoom(z + delta));
  const fitToWidth = () => {
    try {
      const iframe = ref.current;
      const wrapper = wrapperRef.current;
      if (!iframe || !wrapper) return;
      const doc = (iframe.contentWindow as any)?.document as Document | undefined;
      const contentW = doc?.documentElement?.scrollWidth ?? 0;
      const availW = wrapper.clientWidth;
      if (contentW > 0 && availW > 0) setZoom(clampZoom(availW / contentW));
    } catch {}
  };

  // Mirror the tracked highlight id into a ref so the scroll listener (bound once
  // per iframe load) always repositions the latest element.
  const trackRef = useRef<string | null>(null);
  useEffect(() => {
    trackRef.current = highlightTargetId;
  }, [highlightTargetId]);

  // Reposition the highlight frame in sync with preview scrolling (rAF), instead
  // of relying on the 150ms poll which visibly jumps as you scroll.
  useEffect(() => {
    const win = ref.current?.contentWindow as Window | undefined;
    if (!win) return;
    let raf = 0;
    const reposition = () => {
      raf = 0;
      const id = trackRef.current;
      if (!id) return;
      try {
        const doc = (win as any).document as Document | undefined;
        if (!doc) return;
        const el =
          (doc.querySelector(`[data-kuika-id="${id}"]`) as Element | null) ||
          doc.querySelector(`#kuika_${id}, #kuika_${id}-lastChildItem`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        setHighlightBox({ left: r.left, top: r.top, width: r.width, height: r.height, ts: Date.now() });
      } catch {}
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(reposition);
    };
    win.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => {
      try {
        win.removeEventListener("scroll", onScroll, true);
      } catch {}
      if (raf) cancelAnimationFrame(raf);
    };
  }, [iframeKey]);

  // Read-only preview: instrument the (same-origin) iframe document so clicks /
  // edits are swallowed at capture before the app ever sees them.
  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    let bound: Document | null = null;
    const block = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const attach = () => {
      let doc: Document | null = null;
      try {
        doc = iframe.contentDocument ?? (iframe.contentWindow as Window | null)?.document ?? null;
      } catch {
        doc = null;
      }
      if (!doc || doc === bound) return;
      bound = doc;
      doc.addEventListener("click", block, true);
      doc.addEventListener("mousedown", block, true);
      doc.addEventListener("dblclick", block, true);
      doc.addEventListener("contextmenu", block, true);
    };
    const onLoad = () => attach();
    iframe.addEventListener("load", onLoad);
    attach();

    return () => {
      iframe.removeEventListener("load", onLoad);
      try {
        if (bound) {
          bound.removeEventListener("click", block, true);
          bound.removeEventListener("mousedown", block, true);
          bound.removeEventListener("dblclick", block, true);
          bound.removeEventListener("contextmenu", block, true);
        }
      } catch {}
    };
  }, [iframeKey]);

  // Reset the hunk cursor whenever the change set / preview reloads.
  useEffect(() => {
    setHunkIndex(0);
  }, [iframeKey, nonce]);

  // Jump to a changed element (GitHub-style hunk cycling): scroll it into view
  // and frame it with the kind-colored highlight.
  const focusChange = useCallback(
    (index: number) => {
      const ids = kuikaIds ?? [];
      if (!ids.length) return;
      const i = ((index % ids.length) + ids.length) % ids.length;
      setHunkIndex(i);
      const id = ids[i];
      try {
        const w = ref.current?.contentWindow as Window | undefined;
        if (!w) return;
        const doc = (w as any).document as Document | undefined;
        if (!doc) return;
        const el =
          (doc.querySelector(`#kuika_${id}, #kuika_${id}-lastChildItem`) as Element | null) ||
          (doc.querySelector(`[data-kuika-id="${id}"]`) as Element | null);
        if (!el) return;
        try {
          (el as Element).scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        } catch {}
        const rect = el.getBoundingClientRect();
        setHighlightTargetId(String(id));
        setHighlightBox({ left: rect.left, top: rect.top, width: rect.width, height: rect.height, ts: Date.now() });
        w.postMessage(
          {
            designerMessage: {
              messageType: DesignerMessageType.highLightHoveredItemMessage,
              messageId: Math.random().toString(),
              message: { kuikaId: String(id), previewOnly: true } as any
            }
          },
          "*"
        );
      } catch {}
    },
    [kuikaIds]
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handle = () => {
      onLoaded?.();
    };
    el.addEventListener("load", handle);
    return () => el.removeEventListener("load", handle);
  }, [onLoaded]);

  // send snapshot with retry, waiting for iframe readiness when necessary
  const sendPreview = useCallback(
    async (snapshot: any) => {
      // If iframe or its contentWindow isn't ready yet, wait for a short period
      // for the iframe to load before proceeding. This prevents silent early
      // returns when callers trigger a preview before the iframe has mounted.
      if (!ref.current || !ref.current.contentWindow) {
        await new Promise<void>((resolve) => {
          const el = ref.current;
          if (!el) return setTimeout(() => resolve(), 800);
          const onL = () => {
            try {
              el.removeEventListener("load", onL);
            } catch {}
            resolve();
          };
          el.addEventListener("load", onL);
          // safety fallback
          setTimeout(() => {
            try {
              el.removeEventListener("load", onL);
            } catch {}
            resolve();
          }, 1500);
        });
      }
      const win = ref.current?.contentWindow;
      if (!win) {
        // The iframe never came up. Every other exit from here reports back, and the caller keeps
        // its loading state until one of them does — so bailing out silently leaves the preview
        // spinning for good. Say the preview is done, and let the empty viewport be the answer.
        onLoadedRef.current?.();
        return;
      }
      // try to include project from live canvas or window.kuika if available
      const liveCanvas = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
      const project =
        (liveCanvas?.contentWindow as any)?.kuika?.activeKuikaReactInstance?.state?.project ??
        (window as any)?.kuika?.activeKuikaReactInstance?.state?.project;

      let screenDesign: any = normalizeDesign(snapshot);
      if (!screenDesign || !Array.isArray(screenDesign.displayItems)) {
        // Attempt a last-resort unwrap/parse of common wrapper shapes so we
        // can at least post a diagnostic payload for debugging in the user's
        // environment. If we can recover a design object with `displayItems`
        // use it; otherwise post a debug payload that includes the raw snapshot.
        try {
          const raw = snapshot?.JSON ?? snapshot?.Json ?? snapshot?.json ?? snapshot;
          let parsed: any = null;
          if (typeof raw === "string") {
            try {
              parsed = JSON.parse(raw);
            } catch {}
          } else if (typeof raw === "object" && raw != null) {
            parsed = raw;
          }
          if (parsed) {
            const pnorm = normalizeDesign(parsed);
            if (pnorm && Array.isArray(pnorm.displayItems)) screenDesign = pnorm;
            else if (Array.isArray(parsed.displayItems)) screenDesign = parsed;
          }
        } catch {}

        if (!screenDesign || !Array.isArray(screenDesign.displayItems)) {
          try {
            const debugPayload: any = {
              designerMessage: {
                messageType: DesignerMessageType.renderFrameMessage,
                messageId: Math.random().toString(),
                message: {
                  // Keep `screenDesign` undefined so it's obvious the preview
                  // couldn't be constructed, but include the raw snapshot to
                  // aid debugging from the iframe side and in `window` globals.
                  screenDesign: undefined,
                  rawSnapshot: snapshot,
                  project: project ?? undefined,
                  projectTempStyles: project?.projectTempStyles ?? {},
                  dashboardState: DashboardState.design,
                  changeReason: ESdChangeReason.OtherSetStates,
                  previewOnly: true
                } as any
              }
            };
            try {
              win.postMessage(debugPayload, "*");
            } catch (e) {}
          } catch (e) {
            // Payload construction failed silently (debug flow only)
          }
          return;
        }
      }
      const payload = {
        designerMessage: {
          messageType: DesignerMessageType.renderFrameMessage,
          messageId: Math.random().toString(),
          message: {
            screenDesign,
            project: project ?? undefined,
            projectTempStyles: project?.projectTempStyles ?? {},
            dashboardState: DashboardState.design,
            changeReason: ESdChangeReason.OtherSetStates,
            previewOnly: true
          } as IRenderFrameMessage
        }
      };

      // Post the render message and retry until the iframe responds (handshake),
      // or we hit a timeout. This avoids lost messages when the iframe agent
      // isn't ready to receive messages immediately after mount.
      try {
        let ack = false;
        const startTs = Date.now();
        const maxWait = 5000;
        let intervalId: any = null;
        const onMessage = (ev: MessageEvent) => {
          try {
            if (ev.source === win) {
              ack = true;
              onLoadedRef.current?.();
              window.removeEventListener("message", onMessage);
              if (intervalId != null) clearInterval(intervalId);
            }
          } catch {}
        };
        window.addEventListener("message", onMessage);

        // initial post
        try {
          win.postMessage(payload, "*");
        } catch (e) {}

        intervalId = setInterval(() => {
          if (ack || Date.now() - startTs > maxWait) {
            if (!ack) {
              onLoadedRef.current?.();
            }
            try {
              window.removeEventListener("message", onMessage);
            } catch {}
            clearInterval(intervalId);
            return;
          }
          try {
            win.postMessage(payload, "*");
          } catch (e) {}
        }, 500);
      } catch (e) {
        try {
          win.postMessage(payload, "*");
        } catch {}
      }
      // clear any previous highlight timers
      try {
        highlightTimersRef.current.forEach(clearTimeout);
      } catch {}
      highlightTimersRef.current = [];
      // schedule highlights based on current kuikaIds (if provided)
      if (kuikaIds && kuikaIds.length) {
        // For each kuikaId, poll the iframe DOM until the element exists, then post highlight.
        // This avoids posting highlights before the preview has rendered DOM nodes.
        for (let i = 0; i < kuikaIds.length; i++) {
          const id = kuikaIds[i];
          const maxWait = 4000; // ms
          const interval = 150; // ms
          const start = Date.now();
          const check = () => {
            const w = ref.current?.contentWindow as Window | undefined;
            try {
              // attempt to find element by id or lastChild variant
              const doc = w?.document;
              if (doc) {
                // Prefer the data attribute container where multiple children may share the
                // same kuika id; highlight the first child under that container when possible.
                const dataTag = doc.querySelector(`[data-kuika-id="${id}"]`);
                let postId: string | null = null;
                if (dataTag) {
                  const childWithId = dataTag.querySelector('[id^="kuika_"]');
                  const target = childWithId || (dataTag.firstElementChild as Element | null);
                  if (target && (target as Element).id) {
                    postId = (target as Element).id.replace(/^kuika_/, "").replace(/-lastChildItem$/, "");
                  }
                }
                // fallback to direct selector match
                const direct = doc.querySelector(`#kuika_${id}, #kuika_${id}-lastChildItem`);
                if (!postId && direct && (direct as Element).id) {
                  postId = (direct as Element).id.replace(/^kuika_/, "").replace(/-lastChildItem$/, "");
                }
                if (postId) {
                  const payload = {
                    designerMessage: {
                      messageType: DesignerMessageType.highLightHoveredItemMessage,
                      messageId: Math.random().toString(),
                      // Add previewOnly flag so receivers can distinguish
                      // preview-driven highlights from user-selection.
                      message: { kuikaId: postId, previewOnly: true } as any
                    }
                  };
                  if (w) {
                    w.postMessage(payload, "*");
                    // compute overlay position for visual framing
                    try {
                      const doc = (w as any).document as Document | undefined;
                      if (doc) {
                        // try to locate same element we targeted in the iframe
                        let el: Element | null = null;
                        const dataTag2 = doc.querySelector(`[data-kuika-id="${postId}"]`);
                        if (dataTag2)
                          el =
                            (dataTag2.querySelector('[id^="kuika_"]') as Element) ||
                            (dataTag2.firstElementChild as Element | null);
                        if (!el) el = doc.querySelector(`#kuika_${postId}, #kuika_${postId}-lastChildItem`);
                        if (el && wrapperRef.current && ref.current) {
                          const rect = el.getBoundingClientRect();
                          // rect is in the iframe's own viewport space; the highlight box renders
                          // inside the wrapper whose origin coincides with the iframe's top-left.
                          const left = rect.left;
                          const top = rect.top;
                          setHighlightTargetId(String(postId));
                          setHighlightBox({ left, top, width: rect.width, height: rect.height, ts: Date.now() });
                          // clear any existing timers
                          try {
                            if (highlightClearTimeoutRef.current) window.clearTimeout(highlightClearTimeoutRef.current);
                          } catch {}
                          // keep highlight persistent (do not auto-clear)
                          // start periodic updater to follow element while visible
                          try {
                            if (highlightUpdaterRef.current) window.clearInterval(highlightUpdaterRef.current);
                          } catch {}
                          highlightUpdaterRef.current = window.setInterval(() => {
                            try {
                              const el2 =
                                (doc.querySelector(`[data-kuika-id="${postId}"]`) as Element | null) ||
                                doc.querySelector(`#kuika_${postId}, #kuika_${postId}-lastChildItem`);
                              if (!el2) return;
                              const r2 = el2.getBoundingClientRect();
                              const left2 = r2.left;
                              const top2 = r2.top;
                              setHighlightBox({
                                left: left2,
                                top: top2,
                                width: r2.width,
                                height: r2.height,
                                ts: Date.now()
                              });
                            } catch {}
                          }, 150);
                        }
                      }
                    } catch {}
                  }
                  return true;
                }
              }
            } catch (e) {}
            if (Date.now() - start > maxWait) {
              // timeout fallback: post anyway
              const payload = {
                designerMessage: {
                  messageType: DesignerMessageType.highLightHoveredItemMessage,
                  messageId: Math.random().toString(),
                  message: { kuikaId: id, previewOnly: true } as any
                }
              };
              const w = ref.current?.contentWindow;
              if (w) {
                w.postMessage(payload, "*");
                try {
                  const doc = (w as any).document as Document | undefined;
                  if (doc && wrapperRef.current && ref.current) {
                    const el =
                      (doc.querySelector(`[data-kuika-id="${id}"]`) as Element | null) ||
                      doc.querySelector(`#kuika_${id}, #kuika_${id}-lastChildItem`);
                    if (el) {
                      const rect = el.getBoundingClientRect();
                      const left = rect.left;
                      const top = rect.top;
                      setHighlightTargetId(String(id));
                      setHighlightBox({ left, top, width: rect.width, height: rect.height, ts: Date.now() });
                      try {
                        if (highlightClearTimeoutRef.current) window.clearTimeout(highlightClearTimeoutRef.current);
                      } catch {}
                      // persistent highlight on timeout (do not auto-clear)
                      try {
                        if (highlightUpdaterRef.current) window.clearInterval(highlightUpdaterRef.current);
                      } catch {}
                      highlightUpdaterRef.current = window.setInterval(() => {
                        try {
                          const el2 =
                            (doc.querySelector(`[data-kuika-id="${id}"]`) as Element | null) ||
                            doc.querySelector(`#kuika_${id}, #kuika_${id}-lastChildItem`);
                          if (!el2) return;
                          const r2 = el2.getBoundingClientRect();
                          const left2 = r2.left;
                          const top2 = r2.top;
                          setHighlightBox({
                            left: left2,
                            top: top2,
                            width: r2.width,
                            height: r2.height,
                            ts: Date.now()
                          });
                        } catch {}
                      }, 150);
                    }
                  }
                } catch {}
              }
              return true;
            }
            return false;
          };

          // start immediate check and repeat until success or timeout
          if (!check()) {
            const h = setInterval(() => {
              if (check()) clearInterval(h);
            }, interval);
            highlightTimersRef.current.push(h as unknown as ReturnType<typeof setTimeout>);
          }
        }
      }
    },
    [kuikaIds]
  );

  useEffect(() => {
    if (!spec) return;
    let snapshot = getSnapshot(spec.section, spec.mode);

    // If asking for a "before" snapshot and it's missing, attempt to compute
    // it by reversing the section's ops when an "after" snapshot exists.
    if (!snapshot && spec.mode === "before") {
      const afterSnap = getSnapshot(spec.section, "after");
      if (afterSnap) {
        try {
          const afterDesign = normalizeDesign(afterSnap);
          const sourceOps = allOps ?? [];
          let sectionOps: PatchOp[] = [];
          if (spec.section === "consolidated") sectionOps = sourceOps;
          else sectionOps = groupByChangelog(sourceOps)[spec.section as number]?.ops ?? [];
          if (afterDesign && sectionOps.length) {
            const reverseOps: PatchOp[] = sectionOps.map((op) => ({
              ...op,
              op: op.op === "add" ? "remove" : op.op === "remove" ? "add" : op.op,
              value: op.previous,
              previous: op.value
            }));
            const beforeDesign = applyOpsToDesign(afterDesign, reverseOps);
            snapshot = beforeDesign ? { JSON: JSON.stringify(beforeDesign) } : snapshot;
          }
        } catch {}
      }
    }

    sendPreview(snapshot);
    const t1 = setTimeout(() => sendPreview(snapshot), 1000);
    const t2 = setTimeout(() => sendPreview(snapshot), 2500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [spec, getSnapshot, sendPreview, nonce]);

  // highlight scheduling moved into sendPreview so highlights are tied to the
  // exact moment the snapshot is posted. Clear timers on unmount.
  useEffect(() => {
    return () => {
      try {
        highlightTimersRef.current.forEach(clearTimeout);
      } catch {}
      highlightTimersRef.current = [];
      // The highlight updater interval is otherwise cleared only on mouse-leave;
      // clear it (and any pending clear-timeout) on unmount too so the preview
      // overlay doesn't leave a 150ms interval + setState running after teardown.
      try {
        if (highlightUpdaterRef.current) {
          window.clearInterval(highlightUpdaterRef.current);
          highlightUpdaterRef.current = null;
        }
      } catch {}
      try {
        if (highlightClearTimeoutRef.current) {
          window.clearTimeout(highlightClearTimeoutRef.current);
          highlightClearTimeoutRef.current = null;
        }
      } catch {}
    };
  }, []);

  // Helper: get iframe element at viewport client coordinates
  const getIframeElementAt = (clientX: number, clientY: number) => {
    try {
      const iframe = ref.current;
      const wrapper = wrapperRef.current;
      if (!iframe || !wrapper) return null;
      const iframeRect = iframe.getBoundingClientRect();
      // iframeRect is post-transform (scaled); convert the screen offset back into
      // the iframe's own (unscaled) coordinate space before hit-testing.
      const x = (clientX - iframeRect.left) / zoom;
      const y = (clientY - iframeRect.top) / zoom;
      const doc = (iframe.contentWindow as any)?.document as Document | undefined;
      if (!doc) return null;
      const el = doc.elementFromPoint(x, y) as Element | null;
      if (!el) return null;
      // find nearest kuika id by walking up
      let cur: Element | null = el;
      let foundId: string | null = null;
      while (cur && cur !== doc.body) {
        const id = cur.id;
        if (id && id.startsWith("kuika_")) {
          foundId = id.replace(/^kuika_/, "").replace(/-lastChildItem$/, "");
          break;
        }
        const data = cur.getAttribute && cur.getAttribute("data-kuika-id");
        if (data) {
          foundId = data;
          break;
        }
        cur = cur.parentElement;
      }
      const name =
        (el.getAttribute &&
          (el.getAttribute("data-kuika-name") || el.getAttribute("title") || el.getAttribute("aria-label"))) ||
        (el.textContent ? String(el.textContent).trim().split("\n")[0].slice(0, 80) : null);
      const rect = el.getBoundingClientRect();
      return { el, id: foundId, name, rect, iframeRect } as const;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    // focus overlay to capture keyboard and prevent editing in iframe
    try {
      overlayRef.current?.focus();
    } catch {}
  }, [spec, iframeKey]);

  const handleOverlayMove = (e: React.MouseEvent) => {
    try {
      const info = getIframeElementAt(e.clientX, e.clientY);
      setHoverLabel(info?.name ?? null);
    } catch {}
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      const info = getIframeElementAt(e.clientX, e.clientY);
      if (!info) return;
      const left = info.rect.left;
      const top = info.rect.top;
      const idToSet = info.id ?? (info.el.id ? String(info.el.id).replace(/^kuika_/, "") : null);
      if (idToSet) {
        setHighlightTargetId(String(idToSet));
        setHighlightBox({ left, top, width: info.rect.width, height: info.rect.height, ts: Date.now() });
        // post highlight message to iframe
        try {
          const w = ref.current?.contentWindow as Window | undefined;
          if (w) {
            const payload = {
              designerMessage: {
                messageType: DesignerMessageType.highLightHoveredItemMessage,
                messageId: Math.random().toString(),
                message: { kuikaId: String(idToSet), previewOnly: true } as any
              }
            };
            w.postMessage(payload, "*");
          }
        } catch {}
        // start updater to track element movement
        try {
          if (highlightUpdaterRef.current) window.clearInterval(highlightUpdaterRef.current);
        } catch {}
        highlightUpdaterRef.current = window.setInterval(() => {
          try {
            const doc = (ref.current?.contentWindow as any)?.document as Document | undefined;
            if (!doc) return;
            const el2 =
              (doc.querySelector(`#kuika_${idToSet}, #kuika_${idToSet}-lastChildItem`) as Element | null) ||
              (doc.querySelector(`[data-kuika-id="${idToSet}"]`) as Element | null);
            if (!el2 || !wrapperRef.current) return;
            const r2 = el2.getBoundingClientRect();
            setHighlightBox({
              left: r2.left,
              top: r2.top,
              width: r2.width,
              height: r2.height,
              ts: Date.now()
            });
          } catch {}
        }, 150);
      }
    } catch {}
  };

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
    } catch {}
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    try {
      // Block common edit/copy actions while overlay focused
      const blocked = ["c", "x", "v", "a"]; // ctrl/cmd + these
      if ((e.ctrlKey || e.metaKey) && blocked.includes(String(e.key).toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    } catch {}
  };

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={() => {
        try {
          if (highlightClearTimeoutRef.current) {
            window.clearTimeout(highlightClearTimeoutRef.current);
            highlightClearTimeoutRef.current = null;
          }
        } catch {}
      }}
      onMouseLeave={() => {
        try {
          if (highlightUpdaterRef.current) {
            window.clearInterval(highlightUpdaterRef.current);
            highlightUpdaterRef.current = null;
          }
        } catch {}
        try {
          if (highlightClearTimeoutRef.current) {
            window.clearTimeout(highlightClearTimeoutRef.current);
            highlightClearTimeoutRef.current = null;
          }
        } catch {}
        if (!pinned) {
          setHighlightBox(null);
          setHighlightTargetId(null);
        }
      }}
      className="branching-staged__preview-wrapper"
      style={{ overflow: zoom === 1 ? undefined : "hidden" }}
    >
      <iframe
        key={iframeKey}
        ref={ref}
        title="preview"
        src="/agent"
        className="branching-staged__preview-iframe"
        style={{
          display: visible ? "block" : "none",
          // Only deviate from the native 1:1 layout when zoomed. A CSS transform
          // turns the iframe into its own composited scroll layer, which steals
          // wheel events from the overlay-based scroll forwarding — so at 100% we
          // render exactly as before to keep scrolling intact.
          width: zoom === 1 ? "100%" : `${100 / zoom}%`,
          height: zoom === 1 ? "100%" : `${100 / zoom}%`,
          transform: zoom === 1 ? undefined : `scale(${zoom})`
        }}
      />
      {/* Overlay is click-through (pointer-events: none) so the iframe owns
          scrolling and interaction natively — no wrapper-level wheel forwarding,
          no clipping of nested scroll containers. Hover/click handlers remain
          wired for when capture is re-enabled, but stay inert while pass-through. */}
      <div
        ref={overlayRef}
        tabIndex={0}
        onMouseMove={handleOverlayMove}
        onClick={handleOverlayClick}
        onMouseDown={handleOverlayMouseDown}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={handleOverlayKeyDown}
        className="branching-staged__overlay"
      />
      {hoverLabel && <div className="branching-staged__hover-label">{hoverLabel}</div>}
      {/* Hunk navigation: cycle through changed elements (GitHub-style). */}
      {kuikaIds && kuikaIds.length > 0 && (
        <div className="branching-staged__hunknav" onClick={(e) => e.stopPropagation()}>
          <span
            className="branching-staged__hunknav-dot"
            data-kind={changeKinds?.[kuikaIds[hunkIndex]] ?? "modified"}
          />
          <span className="branching-staged__hunknav-count">
            {hunkIndex + 1}
            <span className="branching-staged__hunknav-total"> / {kuikaIds.length}</span>
          </span>
          <span className="branching-staged__hunknav-label">changes</span>
          <span className="branching-staged__hunknav-sep" />
          <KuikaTooltip content="Previous change" position="bottom">
            <KuikaButton
              type="text"
              size="sm"
              isIcon
              disabled={kuikaIds.length < 2}
              onClick={() => focusChange(hunkIndex - 1)}
            >
              <KuikaSvg name="chevron-up" type="arrows" width={15} height={15} />
            </KuikaButton>
          </KuikaTooltip>
          <KuikaTooltip content="Next change" position="bottom">
            <KuikaButton
              type="text"
              size="sm"
              isIcon
              disabled={kuikaIds.length < 2}
              onClick={() => focusChange(hunkIndex + 1)}
            >
              <KuikaSvg name="chevron-down" type="arrows" width={15} height={15} />
            </KuikaButton>
          </KuikaTooltip>
        </div>
      )}
      {highlightBox &&
        (() => {
          const hlKind = highlightTargetId ? changeKinds?.[highlightTargetId] : undefined;
          return (
            <div
              className="branching-staged__highlight-box"
              data-kind={hlKind || undefined}
              style={{
                left: highlightBox.left * zoom,
                top: highlightBox.top * zoom,
                width: highlightBox.width * zoom,
                height: highlightBox.height * zoom
              }}
            />
          );
        })()}
      {/* Zoom controls */}
      <div className="branching-staged__zoom" onClick={(e) => e.stopPropagation()}>
        <KuikaButton type="text" size="sm" isIcon disabled={zoom <= ZOOM_MIN} onClick={() => adjustZoom(-0.1)}>
          <KuikaSvg name="minus" width={15} height={15} />
        </KuikaButton>
        <button type="button" className="branching-staged__zoom-value" title="Reset to 100%" onClick={() => setZoom(1)}>
          {Math.round(zoom * 100)}%
        </button>
        <KuikaButton type="text" size="sm" isIcon disabled={zoom >= ZOOM_MAX} onClick={() => adjustZoom(0.1)}>
          <KuikaSvg name="plus" width={15} height={15} />
        </KuikaButton>
        <span className="branching-staged__zoom-sep" />
        <KuikaTooltip content="Fit width" position="top">
          <KuikaButton type="text" size="sm" isIcon onClick={fitToWidth}>
            <KuikaSvg name="fit-to-view" width={15} height={15} />
          </KuikaButton>
        </KuikaTooltip>
      </div>
    </div>
  );
}

class BranchingStagedChanges extends PureSdComponent<
  IBranchingStagedChangesProps & IWithSDProps,
  IBranchingStagedChangesState
> {
  private previewSnapshotCache = new Map<string, any>();
  private actionInFlight = false;

  /**
   * The commit and discard dialogs' state machine. Both hold their ✓ on the button and only then
   * close — the staged list behind them has already emptied or shed the discarded rows.
   */
  private readonly dialogStatus: SaveStatus = new SaveStatus((dialogStatus: KuikaButtonStatus): void => {
    this.setState({ dialogStatus });
  });
  previewKuikaIds: string[] = [];
  previewChangeKinds: Record<string, ChangeKind> = {};

  constructor(props: IBranchingStagedChangesProps & IWithSDProps) {
    super(props);
    this.state = {
      loading: false,
      items: [],
      selected: null,
      previewSpec: null,
      iframeEntityKey: 0,
      previewNonce: 0,
      previewMode: "after",
      previewLoading: false,
      expandedChangelogIdx: null,
      actionLoading: null,
      showCommitModal: false,
      pendingCommitOpIds: [],
      activeDiffTab: "consolidated",
      inspectorOpen: false,
      inspectorWidth: INSPECTOR_DEFAULT_W,
      inspectorResizing: false,
      pendingDiscard: null,
      dialogStatus: "idle"
    };
  }

  private inspectorResizeStartX = 0;
  private inspectorResizeStartW = 0;

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => false;

  componentDidMount(): void {
    void this.load();
  }

  componentWillUnmount(): void {
    this.detachInspectorResizeListeners();
    this.dialogStatus.dispose();
  }

  private detachInspectorResizeListeners = () => {
    window.removeEventListener("mousemove", this.handleInspectorResizeMove);
    window.removeEventListener("mouseup", this.handleInspectorResizeEnd);
  };

  toggleInspector = () => this.setState((s) => ({ inspectorOpen: !s.inspectorOpen }));

  handleInspectorResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.state.inspectorOpen) return;
    this.inspectorResizeStartX = e.clientX;
    this.inspectorResizeStartW = this.state.inspectorWidth;
    this.setState({ inspectorResizing: true });
    window.addEventListener("mousemove", this.handleInspectorResizeMove);
    window.addEventListener("mouseup", this.handleInspectorResizeEnd);
  };

  handleInspectorResizeMove = (e: MouseEvent) => {
    // Handle sits on the inspector's left edge — drag left to widen.
    const delta = this.inspectorResizeStartX - e.clientX;
    const next = Math.min(INSPECTOR_MAX_W, Math.max(INSPECTOR_MIN_W, this.inspectorResizeStartW + delta));
    this.setState({ inspectorWidth: next });
  };

  handleInspectorResizeEnd = () => {
    this.detachInspectorResizeListeners();
    this.setState({ inspectorResizing: false });
  };

  componentDidUpdate(prevProps: IBranchingStagedChangesProps & IWithSDProps): void {
    if (prevProps.branchId !== this.props.branchId || prevProps.itemsOverride !== this.props.itemsOverride) {
      void this.load();
    }
  }

  private isEntityScreenDesign(entity: StagedEntity | null): boolean {
    return !!entity && Number(entity.entityType) === EJsonEntityType.KScreenDesign;
  }

  private computeChangelogSnapshots(selected: StagedEntity | null): Array<{ before: any; after: any }> {
    if (!selected || !this.isEntityScreenDesign(selected)) return [];
    const allOps = selected.patch ?? [];
    const changelogs = groupByChangelog(allOps);
    const parseSnap = (snap: any) => {
      const norm = normalizeDesign(snap);
      return norm ?? null;
    };
    const beforeBase = parseSnap(selected.beforeSnapshot);
    const afterBase = parseSnap(selected.afterSnapshot);
    if (!beforeBase && afterBase) {
      let current = JSON.parse(JSON.stringify(afterBase));
      const results: Array<{ before: any; after: any }> = [];
      for (let i = changelogs.length - 1; i >= 0; i--) {
        const group = changelogs[i];
        const after = JSON.parse(JSON.stringify(current));
        const reverseOps: PatchOp[] = group.ops.map((op) => ({
          ...op,
          op: op.op === "add" ? "remove" : op.op === "remove" ? "add" : op.op,
          value: op.previous,
          previous: op.value
        }));
        current = applyOpsToDesign(current, reverseOps);
        const before = Array.isArray(current?.displayItems) ? JSON.parse(JSON.stringify(current)) : null;
        results.unshift({
          before: before ? { JSON: JSON.stringify(before) } : null,
          after: { JSON: JSON.stringify(after) }
        });
      }
      return results;
    }
    let currentDesign: any = beforeBase ? JSON.parse(JSON.stringify(beforeBase)) : null;
    return changelogs.map((group) => {
      const beforeDesign = currentDesign ? JSON.parse(JSON.stringify(currentDesign)) : null;
      if (currentDesign) currentDesign = applyOpsToDesign(currentDesign, group.ops);
      const afterDesign = currentDesign ? JSON.parse(JSON.stringify(currentDesign)) : null;
      return {
        before: beforeDesign ? { JSON: JSON.stringify(beforeDesign) } : null,
        after: afterDesign ? { JSON: JSON.stringify(afterDesign) } : null
      };
    });
  }

  private normalizeSnapshot(snap: any): any | null {
    if (!snap) return null;
    if (Array.isArray(snap?.displayItems)) return snap;
    const raw = snap?.JSON ?? snap?.Json ?? snap?.json;
    if (raw != null) {
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed?.displayItems) ? parsed : parsed;
        } catch {
          return null;
        }
      }
      if (typeof raw === "object") {
        return Array.isArray(raw?.displayItems) ? raw : raw;
      }
    }
    return null;
  }

  getSnapshot = (section: number | "consolidated", mode: "before" | "after") => {
    const cacheKey = `${section}:${mode}`;
    const cached = this.previewSnapshotCache.get(cacheKey);
    if (cached) return cached;
    const { selected } = this.state;
    let source: any = null;
    if (section === "consolidated") source = mode === "before" ? selected?.beforeSnapshot : selected?.afterSnapshot;
    else {
      const snaps = this.computeChangelogSnapshots(selected)[section as number];
      source = mode === "before" ? snaps?.before : snaps?.after;
    }
    const norm = this.normalizeSnapshot(source);
    if (norm) return norm;
    return source;
  };

  private getOpIds(ops: PatchOp[]): string[] {
    const ids = new Set<string>();
    for (const o of ops) {
      const id = o.opId ?? o.changeId;
      if (id) ids.add(id);
    }
    return Array.from(ids);
  }

  load = async () => {
    // Read-only override (merge-request review): render the supplied diff, never fetch or save.
    if (this.props.itemsOverride) {
      this.setState({ items: this.props.itemsOverride, loading: false });
      return;
    }
    const { branchId } = this.props;
    if (!branchId) return;
    this.setState({ loading: true });
    try {
      await this.props.sd.saveScreen(true);
      const r = await BranchingService.GetStagedEntities(branchId);
      if (r?.data) this.setState({ items: r.data as StagedEntity[] });
    } catch (e) {
      // Error handled by service
    } finally {
      this.setState({ loading: false });
    }
  };

  handleAcceptAll = async () => {
    const { branchId } = this.props;
    const { items } = this.state;
    if (!branchId || this.actionInFlight) return;
    const ids = this.getOpIds(items.flatMap((it) => it.patch ?? []));
    if (!ids.length) return;
    this.setState({ pendingCommitOpIds: ids, showCommitModal: true });
  };

  handleCloseCommitModal = () => {
    if (this.state.actionLoading === "accept" || !this.dialogStatus.canStart) return;
    this.setState({ showCommitModal: false, pendingCommitOpIds: [] });
  };

  /**
   * The dialog used to close in `finally`, so a refused commit took the dialog — and the message
   * typed into it — away with it. It now holds its ✓ on COMMIT and closes only on success; a
   * refusal stays on the button as "Try again" (the service has said why).
   */
  handleConfirmCommitModal = async (payload: CommitModalPayload) => {
    const { branchId } = this.props;
    if (!branchId || this.actionInFlight || !this.dialogStatus.canStart) return;
    this.actionInFlight = true;
    this.dialogStatus.start();
    this.setState({ actionLoading: "accept" });
    try {
      const commitRes = await BranchingService.CommitStaged(branchId, payload.commitMessage);
      if (commitRes) {
        await this.load();
        this.setState({ selected: null });
        this.dialogStatus.succeed((): void => {
          this.setState({ showCommitModal: false, pendingCommitOpIds: [] });
        });
      } else {
        this.dialogStatus.fail();
      }
    } catch (e) {
      // Error handled by service
      this.dialogStatus.fail();
    } finally {
      this.actionInFlight = false;
      this.setState({ actionLoading: null });
    }
  };

  // Discarding staged changes is irreversible, so both entry points open a confirmation modal (#15).
  // The actual discard runs from confirmDiscard → performRevert*.
  handleRevertAll = () => {
    const { branchId } = this.props;
    const { selected } = this.state;
    if (!selected || !branchId || this.actionInFlight) return;
    const ids = this.getOpIds(selected.patch ?? []);
    if (!ids.length) return;
    this.setState({ pendingDiscard: { kind: "all", count: ids.length } });
  };

  handleRevertChangelog = (idx: number) => {
    const { branchId } = this.props;
    const { selected } = this.state;
    if (!selected || !branchId || this.actionInFlight) return;
    const allChangelogs = groupByChangelog(selected.patch ?? []);
    const opsToDiscard = allChangelogs.slice(idx).flatMap((cl) => cl.ops);
    const ids = this.getOpIds(opsToDiscard);
    if (!ids.length) return;
    this.setState({ pendingDiscard: { kind: "changelog", idx, count: ids.length } });
  };

  private cancelDiscard = () => {
    if (!this.dialogStatus.canStart) return;
    this.setState({ pendingDiscard: null });
  };

  /**
   * The confirmation used to close before the discard had even left, so a refusal had no
   * surface to be said on. It now stays up, DISCARD saying where it stands, and closes after
   * its ✓ — the discarded rows have left the list behind it by then.
   */
  private confirmDiscard = async () => {
    const pending = this.state.pendingDiscard;
    if (!pending || !this.dialogStatus.canStart) return;
    this.dialogStatus.start();
    const outcome: DiscardOutcome =
      pending.kind === "all" ? await this.performRevertAll() : await this.performRevertChangelog(pending.idx);
    if (outcome === "failed") {
      this.dialogStatus.fail();
      return;
    }
    if (outcome === "skipped") {
      this.dialogStatus.reset();
      this.setState({ pendingDiscard: null });
      return;
    }
    this.dialogStatus.succeed((): void => {
      this.setState({ pendingDiscard: null });
    });
  };

  private performRevertAll = async (): Promise<DiscardOutcome> => {
    const { branchId, onAfterDiscard } = this.props;
    const { selected } = this.state;
    if (!selected || !branchId || this.actionInFlight) return "skipped";
    const ids = this.getOpIds(selected.patch ?? []);
    if (!ids.length) return "skipped";
    this.actionInFlight = true;
    this.setState({ actionLoading: "revert" });
    try {
      const res = await BranchingService.DiscardStagedOps(branchId, ids);
      if (res?.data) {
        await this.load();
        this.setState({ selected: null });
        onAfterDiscard?.();
        return "discarded";
      }
      return "failed";
    } catch (e) {
      // Error handled by service
      return "failed";
    } finally {
      this.actionInFlight = false;
      this.setState({ actionLoading: null });
    }
  };

  private performRevertChangelog = async (idx: number): Promise<DiscardOutcome> => {
    const { branchId, onAfterDiscard } = this.props;
    const { selected } = this.state;
    if (!selected || !branchId || this.actionInFlight) return "skipped";
    const allChangelogs = groupByChangelog(selected.patch ?? []);
    const opsToDiscard = allChangelogs.slice(idx).flatMap((cl) => cl.ops);
    const ids = this.getOpIds(opsToDiscard);
    if (!ids.length) return "skipped";
    this.actionInFlight = true;
    const key = `revert-cl-${idx}`;
    this.setState({ actionLoading: key });
    try {
      const res = await BranchingService.DiscardStagedOps(branchId, ids);
      if (res?.data) {
        await this.load();
        this.setState({ selected: null });
        onAfterDiscard?.();
        return "discarded";
      }
      return "failed";
    } catch (e) {
      // Error handled by service
      return "failed";
    } finally {
      this.actionInFlight = false;
      this.setState({ actionLoading: null });
    }
  };

  handleSelect = (entity: StagedEntity) => {
    this.previewSnapshotCache.clear();
    const isScreen = this.isEntityScreenDesign(entity);
    this.setState(
      (s) => ({
        selected: entity,
        previewSpec: null,
        inspectorOpen: false,
        expandedChangelogIdx: null,
        previewMode: "after" as const,
        iframeEntityKey: isScreen ? s.iframeEntityKey + 1 : s.iframeEntityKey
      }),
      () => {
        // Preview-first: load the consolidated "after" view immediately so the
        // canvas is the base surface; the diff is opt-in via the inspector.
        if (isScreen) this.handleSetPreview("consolidated", "after");
      }
    );
  };

  handleSetPreview = (section: number | "consolidated", mode: "before" | "after") => {
    const { selected } = this.state;
    const sectionOps =
      section === "consolidated"
        ? (selected?.patch ?? [])
        : (groupByChangelog(selected?.patch ?? [])[section as number]?.ops ?? []);
    const changed = extractChangedKuikaIds(sectionOps, this.getSnapshot(section, mode));
    this.previewKuikaIds = changed.map((c) => c.id);
    this.previewChangeKinds = Object.fromEntries(changed.map((c) => [c.id, c.kind]));

    try {
      if (section === "consolidated" && mode === "before") {
        const existing = this.getSnapshot("consolidated", "before");
        if (!existing) {
          const afterSnap = this.getSnapshot("consolidated", "after");
          if (afterSnap) {
            const afterDesign = normalizeDesign(afterSnap);
            const allOps: PatchOp[] = selected?.patch ?? [];
            if (afterDesign && allOps.length) {
              const reverseOps: PatchOp[] = allOps.map((op) => ({
                ...op,
                op: op.op === "add" ? "remove" : op.op === "remove" ? "add" : op.op,
                value: op.previous,
                previous: op.value
              }));
              const beforeDesign = applyOpsToDesign(afterDesign, reverseOps);
              if (beforeDesign && Array.isArray(beforeDesign.displayItems)) {
                this.previewSnapshotCache.set("consolidated:before", beforeDesign);
              }
            }
          }
        }
      }
    } catch (e) {
      // silent catch
    }

    this.setState((s) => ({
      previewLoading: true,
      previewSpec: { section, mode },
      previewNonce: s.previewNonce + 1
    }));
  };

  handleSetSection = (section: number | "consolidated", modeOverride?: "before" | "after") => {
    this.handleSetPreview(section, modeOverride ?? this.state.previewMode);
  };

  handleModeChange = (mode: "before" | "after") => {
    const { selected, previewSpec } = this.state;
    const section = previewSpec?.section ?? (this.isEntityScreenDesign(selected) ? "consolidated" : null);
    this.setState({ previewMode: mode });
    if (section != null) this.handleSetPreview(section, mode);
  };

  render() {
    const {
      loading,
      items,
      selected,
      previewSpec,
      iframeEntityKey,
      previewNonce,
      previewMode,
      previewLoading,
      expandedChangelogIdx,
      actionLoading,
      showCommitModal,
      pendingCommitOpIds,
      activeDiffTab,
      inspectorOpen,
      inspectorWidth,
      inspectorResizing,
      pendingDiscard
    } = this.state;

    // The grouped list in outline while it has nothing to show. A reload after a commit or a
    // discard keeps the list on screen — it used to swap it for a spinner every time.
    if (resolveLoadingPresentation(loading, items.length > 0) === "skeleton") {
      return (
        <KuikaSkeletonRegion label="Loading changes" className="branching-staged__skeleton">
          <KuikaSkeletonList rows={6} withIcon lines={2} />
        </KuikaSkeletonRegion>
      );
    }

    const grouped: Record<string, StagedEntity[]> = {};
    for (const item of items) {
      const key = item.entityType ?? "Unknown";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    }

    // Build KDatasource ID set and action-to-datasource map for nested display
    const stagedDsIds = new Set<string>();
    const dsKey = String(EJsonEntityType.KDatasource);
    const actionKey = String(EJsonEntityType.KAction);
    if (grouped[dsKey]) {
      for (const ds of grouped[dsKey]) stagedDsIds.add(ds.entityId);
    }
    const actionsByDsId: Record<string, StagedEntity[]> = {};
    const standaloneActions: StagedEntity[] = [];
    if (grouped[actionKey]) {
      for (const action of grouped[actionKey]) {
        const snap = action.afterSnapshot ?? action.beforeSnapshot;
        const parsed = snap ? parseSnapshotForDsId(snap) : null;
        if (parsed && stagedDsIds.has(parsed)) {
          if (!actionsByDsId[parsed]) actionsByDsId[parsed] = [];
          actionsByDsId[parsed].push(action);
        } else {
          standaloneActions.push(action);
        }
      }
    }

    const selectedName = selected ? ((selected as any)["entityLabel"] ?? selected.entityId) : "";
    const changelogSnapshots = this.computeChangelogSnapshots(selected);
    const filteredPatch = selected ? filterRedundantXmlOps(selected.patch ?? [], selected.entityType) : [];
    const changelogs = selected ? groupByChangelog(filteredPatch) : [];
    const isScreenDesign = this.isEntityScreenDesign(selected);

    const renderStagedRow = (e: StagedEntity, indent = false) => {
      const name = (e as any)["entityLabel"] ?? e.entityId;
      const created = formatTs((e as any)["timestampUtc"] ?? (e as any)["createdUtc"]);
      const isSelected = selected?.entityId === e.entityId;
      const kind = deriveChangeKind(e);
      const meta = CHANGE_KIND_META[kind];
      return (
        <div
          key={e.entityId}
          className="branching-staged__row"
          data-selected={isSelected || undefined}
          data-indent={indent || undefined}
          onClick={() => this.handleSelect(e)}
        >
          <KuikaTooltip content={meta.label} position="right">
            <span className="branching-staged__row-kind" data-kind={kind}>
              {meta.glyph}
            </span>
          </KuikaTooltip>
          <span className="branching-staged__row-text">
            <span className="branching-staged__row-name">{name}</span>
            {created && <span className="branching-staged__row-meta">{created}</span>}
          </span>
        </div>
      );
    };

    return (
      <div className="branching-staged__layout">
        {/* ── Left panel: entity list (full height) ──────────────────── */}
        <div className="branching-staged__sidebar">
          {!this.props.readOnly && (
            <div className="branching-staged__sidebar-action">
              <KuikaButton
                type="primary"
                size="sm"
                loading={actionLoading === "accept"}
                disabled={!!actionLoading || items.length === 0}
                onClick={this.handleAcceptAll}
                data-testid="branchingCommitAllButton"
              >
                <span className="branching-staged__commit-all">
                  <KuikaSvg name="git-commit" width={14} height={14} />
                  Commit All
                  {items.length > 0 && <span className="branching-staged__count">{items.length}</span>}
                </span>
              </KuikaButton>
            </div>
          )}
          <div className="branching-staged__list">
            {items.length === 0 ? (
              <div className="branching-staged__empty">
                <div className="branching-staged__empty-icon">
                  <KuikaSvg name="git-commit" width={22} height={22} />
                </div>
                <span className="branching-staged__empty-title">No staged changes</span>
                <span className="branching-staged__empty-sub">You&apos;re all caught up.</span>
              </div>
            ) : (
              Object.entries(grouped).map(([type, entities]) => {
                // Skip KAction group here — actions are rendered nested under KDatasource or standalone below
                if (type === actionKey) return null;

                return (
                  <div key={type}>
                    <div className="branching-staged__group">{entityTypeLabel(Number(type))}</div>
                    {entities.map((e) => (
                      <React.Fragment key={e.entityId}>
                        {renderStagedRow(e)}
                        {/* Nest child actions under their parent datasource */}
                        {type === dsKey && actionsByDsId[e.entityId]?.map((action) => renderStagedRow(action, true))}
                      </React.Fragment>
                    ))}
                  </div>
                );
              })
            )}
            {/* Standalone actions (not linked to a staged datasource) */}
            {standaloneActions.length > 0 && (
              <div>
                <div className="branching-staged__group">{entityTypeLabel(0)}</div>
                {standaloneActions.map((e) => renderStagedRow(e))}
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: diff/tabs + bottom drawer ─────────────────── */}
        <div className="branching-staged__detail-column">
          {!selected ? (
            <div className="branching-staged__empty branching-staged__empty--detail">
              <div className="branching-staged__empty-icon">
                <KuikaSvg name="branching" type="topbar-icons" width={22} height={22} />
              </div>
              <span className="branching-staged__empty-title">No change selected</span>
              <span className="branching-staged__empty-sub">Pick an item on the left to preview it.</span>
            </div>
          ) : (
            <>
              {/* Entity header */}
              <div className="branching-staged__detail-header">
                <div className={`branching-staged__detail-headrow ${OVERFLOW_REVEAL_HOST_CLASS}`}>
                  <div className="branching-staged__detail-titles">
                    <span className="branching-staged__detail-name">
                      <KuikaOverflowText>{selectedName}</KuikaOverflowText>
                    </span>
                    <span className="branching-staged__detail-meta">
                      {entityTypeLabel(Number(selected.entityType))} • {selected.patch?.length ?? 0} ops
                      {(selected as any)?.author ? ` • ${(selected as any).author}` : ""}
                    </span>
                  </div>
                  <div className="branching-staged__detail-actions">
                    {isScreenDesign && (
                      <KuikaButton type="secondary" size="sm" active={inspectorOpen} onClick={this.toggleInspector}>
                        <span className="branching-staged__btn-label">
                          <KuikaSvg name="list-tree" type="sidebar-icons" width={15} height={15} />
                          {inspectorOpen ? "Hide changes" : "Changes"}
                          {changelogs.length > 0 && (
                            <span className="branching-staged__inspect-count">{changelogs.length}</span>
                          )}
                        </span>
                      </KuikaButton>
                    )}
                    {!this.props.readOnly && (
                      <KuikaButton
                        type="secondary"
                        color="danger"
                        size="sm"
                        loading={actionLoading === "revert"}
                        disabled={!!actionLoading}
                        onClick={this.handleRevertAll}
                      >
                        <span className="branching-staged__btn-label">
                          <KuikaSvg name="arrow-back-up" type="arrows" width={15} height={15} />
                          Revert
                        </span>
                      </KuikaButton>
                    )}
                  </div>
                </div>
              </div>

              {/* Non-screen entities: semantic diff card is the primary view (nothing to preview). */}
              {!isScreenDesign && selected && (
                <div className="branching-staged__diffcard">
                  {Number(selected.entityType) === EJsonEntityType.KWorkFlow ? (
                    <WorkflowChangeCaptions selected={selected} items={items} />
                  ) : (
                    <EntityDiffCard
                      entityType={Number(selected.entityType)}
                      beforeSnapshot={selected.beforeSnapshot}
                      afterSnapshot={selected.afterSnapshot}
                      mode="staged"
                      allStagedEntities={items}
                    />
                  )}
                </div>
              )}

              {/* Screen entities: live preview is the base surface; the diff lives in an opt-in inspector. */}
              {isScreenDesign && (
                <div className="branching-staged__stage">
                  <div className="branching-staged__viewport">
                    <div className="branching-staged__viewport-bar">
                      <div className="branching-staged__beforeafter">
                        <KuikaTabs
                          size="sm"
                          value={previewMode}
                          onChange={(v) => this.handleModeChange(v as "before" | "after")}
                          options={[
                            { value: "after", label: "After" },
                            { value: "before", label: "Before" }
                          ]}
                        />
                      </div>
                      {previewSpec && (
                        <span className="branching-staged__viewport-section">
                          {previewSpec.section === "consolidated"
                            ? "Consolidated changes"
                            : `Change #${(previewSpec.section as number) + 1}`}
                        </span>
                      )}
                    </div>
                    <div className="branching-staged__viewport-canvas">
                      {previewLoading && (
                        <div className="branching-staged__preview-loading">
                          {/* The preview's shape is not known until it renders: the logo, not an outline. */}
                          <KuikaLoader variant="logo" size="md" label="Loading preview" />
                        </div>
                      )}
                      <SectionPreview
                        iframeKey={iframeEntityKey}
                        spec={previewSpec}
                        getSnapshot={this.getSnapshot}
                        onLoaded={() => this.setState({ previewLoading: false })}
                        kuikaIds={this.previewKuikaIds}
                        changeKinds={this.previewChangeKinds}
                        nonce={previewNonce}
                        allOps={selected?.patch ?? []}
                      />
                    </div>
                  </div>
                  <aside
                    className="branching-staged__inspector"
                    data-open={inspectorOpen || undefined}
                    data-resizing={inspectorResizing || undefined}
                    style={{ width: inspectorWidth }}
                  >
                    <div
                      className="branching-staged__inspector-resize"
                      onMouseDown={this.handleInspectorResizeStart}
                      title="Drag to resize"
                    />
                    <div className="branching-staged__inspector-header">
                      <span className="branching-staged__inspector-title">Changes</span>
                      <KuikaButton type="text" size="sm" isIcon onClick={this.toggleInspector}>
                        <KuikaSvg name="x" width={15} height={15} />
                      </KuikaButton>
                    </div>
                    <div className="branching-staged__inspector-body">
                      <div className="branching-staged__difftabs">
                        <KuikaTabs
                          size="sm"
                          value={activeDiffTab}
                          onChange={(key) => {
                            this.setState({ activeDiffTab: key as "consolidated" | "changelogs" });
                            if (key === "consolidated") this.handleSetSection("consolidated");
                          }}
                          options={[
                            { value: "consolidated", label: "Consolidated" },
                            {
                              value: "changelogs",
                              label: `Changelogs${changelogs.length ? ` (${changelogs.length})` : ""}`
                            }
                          ]}
                        />
                      </div>
                      <div className="branching-staged__difftab-body">
                        {activeDiffTab === "consolidated" && (
                          <DiffTable
                            ops={
                              buildConsolidated(
                                filterRedundantXmlOps(selected.patch ?? [], selected.entityType)
                              ) as PatchOp[]
                            }
                            snapshotAfter={selected.afterSnapshot}
                            snapshotBefore={selected.beforeSnapshot}
                            entityType={selected.entityType}
                          />
                        )}

                        {activeDiffTab === "changelogs" &&
                          (changelogs.length === 0 ? (
                            <div className="branching-staged__inline-empty">No changelogs</div>
                          ) : (
                            <div className="branching-staged__changelog-list">
                              {changelogs.map((cl, idx) => {
                                const isExpanded = expandedChangelogIdx === idx;
                                const snap = changelogSnapshots[idx];
                                return (
                                  <div
                                    key={cl.changeId}
                                    className="branching-staged__changelog"
                                    data-expanded={isExpanded || undefined}
                                  >
                                    <div
                                      className="branching-staged__changelog-head"
                                      onClick={() => {
                                        const next = isExpanded ? null : idx;
                                        this.setState({ expandedChangelogIdx: next });
                                        if (next !== null) this.handleSetSection(next);
                                      }}
                                    >
                                      <div className="branching-staged__changelog-info">
                                        <span className="branching-staged__changelog-title">Change #{idx + 1}</span>
                                        {cl.author && (
                                          <span className="branching-staged__changelog-author">{cl.author}</span>
                                        )}
                                        {cl.timestampUtc && (
                                          <span className="branching-staged__changelog-ts">
                                            {formatTs(cl.timestampUtc)}
                                          </span>
                                        )}
                                        <span className="branching-staged__changelog-count">{cl.ops.length}</span>
                                      </div>
                                      <div
                                        className="branching-staged__changelog-actions"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {isScreenDesign && !this.props.readOnly && (
                                          <KuikaTooltip
                                            content={
                                              idx < changelogs.length - 1
                                                ? `Reverts Change #${idx + 1} and all ${changelogs.length - 1 - idx} subsequent change(s) to maintain consistency`
                                                : "Revert this change"
                                            }
                                          >
                                            <KuikaButton
                                              type="secondary"
                                              color="danger"
                                              size="sm"
                                              loading={actionLoading === `revert-cl-${idx}`}
                                              disabled={!!actionLoading}
                                              onClick={() => this.handleRevertChangelog(idx)}
                                            >
                                              {idx < changelogs.length - 1 ? `Revert from #${idx + 1}` : "Revert"}
                                            </KuikaButton>
                                          </KuikaTooltip>
                                        )}
                                        <KuikaSvg
                                          name="chevron-down"
                                          type="arrows"
                                          width={14}
                                          height={14}
                                          className="branching-staged__chevron"
                                        />
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      <div className="branching-staged__changelog-body">
                                        <DiffTable
                                          ops={filterRedundantXmlOps(cl.ops as PatchOp[], selected?.entityType)}
                                          snapshotAfter={snap?.after}
                                          snapshotBefore={snap?.before}
                                          entityType={selected?.entityType}
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                      </div>
                    </div>
                  </aside>
                </div>
              )}
            </>
          )}
        </div>
        {/* end right column */}

        {/* Drag shield: while resizing, cover the iframe so it can't swallow
            mousemove/mouseup and cut the drag short. */}
        {inspectorResizing && <div className="branching-staged__drag-shield" />}

        <CommitToBranchModal
          showModal={showCommitModal}
          onClose={this.handleCloseCommitModal}
          onCommit={this.handleConfirmCommitModal}
          checkedItemsCount={pendingCommitOpIds.length}
          inProgress={actionLoading === "accept"}
          status={this.state.dialogStatus}
        />

        <DiscardChangesModal
          showModal={!!pendingDiscard}
          selectedCount={pendingDiscard?.count ?? 0}
          onClose={this.cancelDiscard}
          onDiscardChanges={this.confirmDiscard}
          status={this.state.dialogStatus}
        />
      </div>
    );
  }
}

export default withSD<IBranchingStagedChangesProps>(BranchingStagedChanges);

function formatTs(ts?: string): string {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-US");
}

type ChangeKind = "added" | "modified" | "deleted";

/** Derive a Git-style change kind from snapshot presence for list affordances. */
function deriveChangeKind(e: StagedEntity): ChangeKind {
  const hasBefore = e.beforeSnapshot != null && e.beforeSnapshot !== "";
  const hasAfter = e.afterSnapshot != null && e.afterSnapshot !== "";
  if (!hasBefore && hasAfter) return "added";
  if (hasBefore && !hasAfter) return "deleted";
  return "modified";
}

const CHANGE_KIND_META: Record<ChangeKind, { glyph: string; label: string }> = {
  added: { glyph: "A", label: "Added" },
  modified: { glyph: "M", label: "Modified" },
  deleted: { glyph: "D", label: "Deleted" }
};

interface ChangedId {
  id: string;
  kind: ChangeKind;
}

function opToChangeKind(op: PatchOp): ChangeKind {
  if (op.op === "add") return "added";
  if (op.op === "remove") return "deleted";
  return "modified";
}

/** Extract KDatasource_Id from a staged entity snapshot for action-datasource grouping. */
function parseSnapshotForDsId(snapshot: unknown): string | null {
  if (!snapshot) return null;
  let obj = snapshot as Record<string, unknown>;
  if (typeof snapshot === "string") {
    try {
      obj = JSON.parse(snapshot);
    } catch {
      return null;
    }
  }
  const raw = obj.JSON ?? obj.Json ?? obj.json;
  if (raw && typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  } else if (raw && typeof raw === "object") {
    obj = raw as Record<string, unknown>;
  }
  const dsId = obj.KDatasource_Id ?? obj.kDatasource_Id;
  return dsId ? String(dsId) : null;
}

const entityTypeLabel = (type: number | string | undefined): string => {
  if (type === undefined || type === null) return "Unknown";
  const n = typeof type === "string" ? parseInt(type, 10) : type;
  return ENTITY_TYPE_DISPLAY_NAMES[n] ?? String(type);
};
